import type {
  DescMessage,
  DescMethodUnary,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf'
import type { CallOptions, ConnectError, Transport } from '@connectrpc/connect'
import type { ConnectQueryKey, SkipToken } from '@connectrpc/connect-query'
import {
  callUnaryMethod,
  createConnectQueryKey,
  useQuery,
  useSuspenseQuery,
} from '@connectrpc/connect-query'
import type {
  QueryFilters,
  UseQueryResult,
  UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { queryClient } from '@jotta/query'

type Options<I extends DescMessage, O extends DescMessage, SELECT> = Parameters<
  typeof useQuery<I, O, SELECT>
>[2] & {
  variables?: SkipToken | MessageInitShape<I>
}

export type ConnectQueryHook<I extends DescMessage, O extends DescMessage> = {
  <SELECT = MessageShape<O>>(
    options?: Options<I, O, SELECT>,
  ): UseQueryResult<SELECT, ConnectError>
  fetch: (
    request: MessageInitShape<I>,
    options: {
      transport: Transport
      callOptions?: CallOptions | undefined
    },
  ) => Promise<MessageShape<O>>
  descriptor: DescMethodUnary<I, O>
  getKey: (input?: SkipToken | MessageInitShape<I>) => ConnectQueryKey<O>
  invalidate: (input?: SkipToken | MessageInitShape<I>) => Promise<void>
  reset: (input?: SkipToken | MessageInitShape<I>) => Promise<void>
  refetch: (input?: SkipToken | MessageInitShape<I>) => Promise<void>
  suspense: (
    input: MessageInitShape<I>,
    options?: Parameters<typeof useSuspenseQuery<I, O>>[2],
  ) => UseSuspenseQueryResult<MessageShape<O>, ConnectError>
  setQueryData: (
    input: MessageInitShape<I>,
    data: MessageShape<O>,
    options?: Parameters<typeof queryClient.setQueryData>[2],
  ) => void
}

export function createConnectQuery<
  I extends DescMessage,
  O extends DescMessage,
>(
  methodSig: DescMethodUnary<I, O>,
  defaultOptions?: Omit<Parameters<typeof useQuery<I, O>>[2], 'select'>,
): ConnectQueryHook<I, O> {
  function getKey(input?: SkipToken | MessageInitShape<I>): ConnectQueryKey<O> {
    return createConnectQueryKey<I, O>({
      schema: methodSig,
      input: input,
      cardinality: undefined,
    })
  }

  function filters(
    input: SkipToken | MessageInitShape<I> | undefined,
  ): QueryFilters {
    const queryKey = getKey(input)
    return {
      queryKey: input === undefined ? queryKey.slice(0, 2) : queryKey,
    }
  }

  function useConnectQueryHook<SELECT = MessageShape<O>>(
    input?: Options<I, O, SELECT>,
  ) {
    const { variables, ...options } = input || {}
    return useQuery(methodSig, variables, {
      ...defaultOptions,
      ...options,
    })
  }
  function useConnectSuspenseQueryHook(
    input: MessageInitShape<I>,
    options?: Parameters<typeof useSuspenseQuery<I, O>>[2],
  ) {
    return useSuspenseQuery(methodSig, input, options)
  }
  const fn = useConnectQueryHook as ConnectQueryHook<I, O>
  fn.suspense = useConnectSuspenseQueryHook
  fn.descriptor = methodSig
  fn.fetch = (request, { transport, callOptions }) =>
    queryClient.fetchQuery({
      queryKey: getKey(request),
      queryFn: () =>
        callUnaryMethod(transport, methodSig, request, callOptions),
    })
  fn.getKey = getKey
  fn.invalidate = input => queryClient.invalidateQueries(filters(input))
  fn.reset = input => queryClient.resetQueries(filters(input))
  fn.refetch = input => queryClient.refetchQueries(filters(input))
  fn.setQueryData = (input, data, options) =>
    queryClient.setQueryData(getKey(input), data, options)

  return fn
}
